/**
 * analytics.ts
 * Real-time analytics service — Supabase real-time subscriptions,
 * tracking pixel helper, click-tracking URL builder, and dashboard aggregation.
 *
 * Falls back to in-memory state when Supabase is INACTIVE / unreachable.
 */

import { supabase } from '@/integrations/supabase/client';
import type { RealtimeChannel } from '@supabase/supabase-js';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface AnalyticsEvent {
  id?: string;
  campaign_id: string | null;
  platform: string;
  event_type: 'click' | 'view' | 'share' | 'open' | 'publish_success' | 'publish_error';
  tracking_pixel_id?: string | null;
  metadata?: Record<string, unknown>;
  timestamp?: string;
}

export interface PlatformStat {
  platform: string;
  clicks:   number;
  views:    number;
  shares:   number;
  opens:    number;
  success:  number;
  errors:   number;
  total:    number;
}

export interface CampaignRow {
  id: string;
  content_payload:    Record<string, unknown>;
  platforms_targeted: string[];
  status: 'draft' | 'scheduled' | 'publishing' | 'published' | 'failed';
  created_at: string;
  updated_at: string;
}

// ── Event ingestion ───────────────────────────────────────────────────────────

/**
 * Insert a single analytics event. Silently ignores failures (DB inactive etc.)
 */
export async function trackEvent(event: AnalyticsEvent): Promise<void> {
  try {
    const { error } = await supabase
      .from('analytics_events')
      .insert({
        campaign_id:       event.campaign_id,
        platform:          event.platform,
        event_type:        event.event_type,
        tracking_pixel_id: event.tracking_pixel_id ?? null,
        metadata:          event.metadata ?? {},
      });
    if (error) {
      console.warn('[analytics] insert failed:', error.message);
    }
  } catch {
    // Supabase inactive — silently skip
  }
}

// ── Campaign management ───────────────────────────────────────────────────────

export async function createCampaign(
  contentPayload: Record<string, unknown>,
  platformsTargeted: string[],
): Promise<string | null> {
  try {
    const { data, error } = await supabase
      .from('campaign_distribution')
      .insert({ content_payload: contentPayload, platforms_targeted: platformsTargeted, status: 'draft' })
      .select('id')
      .single();
    if (error) { console.warn('[analytics] createCampaign failed:', error.message); return null; }
    return (data as { id: string }).id ?? null;
  } catch {
    return null;
  }
}

export async function updateCampaignStatus(
  id: string,
  status: CampaignRow['status'],
): Promise<void> {
  try {
    const { error } = await supabase
      .from('campaign_distribution')
      .update({ status })
      .eq('id', id);
    if (error) console.warn('[analytics] updateCampaignStatus failed:', error.message);
  } catch { /* inactive */ }
}

// ── Dashboard aggregation ─────────────────────────────────────────────────────

/**
 * Fetch and aggregate analytics_events grouped by platform + event_type.
 * Returns empty array on failure.
 */
export async function fetchPlatformStats(campaignId?: string): Promise<PlatformStat[]> {
  try {
    let query = supabase
      .from('analytics_events')
      .select('platform, event_type, campaign_id')
      .limit(5000);

    if (campaignId) query = query.eq('campaign_id', campaignId);

    const { data, error } = await query;
    if (error) { console.warn('[analytics] fetchPlatformStats error:', error.message); return []; }

    // Aggregate client-side
    const agg: Record<string, Record<string, number>> = {};
    for (const row of (data ?? []) as AnalyticsEvent[]) {
      if (!agg[row.platform]) agg[row.platform] = {};
      agg[row.platform][row.event_type] = (agg[row.platform][row.event_type] ?? 0) + 1;
    }

    return Object.entries(agg).map(([platform, events]) => ({
      platform,
      clicks:  events['click']           ?? 0,
      views:   events['view']            ?? 0,
      shares:  events['share']           ?? 0,
      opens:   events['open']            ?? 0,
      success: events['publish_success'] ?? 0,
      errors:  events['publish_error']   ?? 0,
      total:   Object.values(events).reduce((s, v) => s + v, 0),
    }));
  } catch {
    return [];
  }
}

// ── Real-time subscription ────────────────────────────────────────────────────

type StatChangeCallback = (stats: PlatformStat[]) => void;

let _channel: RealtimeChannel | null = null;

/**
 * Subscribe to analytics_events real-time changes.
 * Calls `onChange` with freshly aggregated stats on every INSERT.
 * Returns an unsubscribe function.
 */
export function subscribeToAnalytics(onChange: StatChangeCallback): () => void {
  // Unsubscribe any prior channel
  if (_channel) {
    supabase.removeChannel(_channel);
    _channel = null;
  }

  try {
    _channel = supabase
      .channel('analytics_events_realtime')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'analytics_events' },
        async () => {
          const stats = await fetchPlatformStats();
          onChange(stats);
        },
      )
      .subscribe();
  } catch {
    // Supabase inactive — subscription silently fails
  }

  return () => {
    if (_channel) {
      supabase.removeChannel(_channel);
      _channel = null;
    }
  };
}

// ── Tracking pixel helper ─────────────────────────────────────────────────────

/**
 * Returns the tracking pixel URL for a campaign + platform combo.
 * Uses the Supabase Edge Function when available, else falls back to a no-op.
 */
export function buildPixelUrl(campaignId: string, platform: string): string {
  const base = import.meta.env.VITE_SUPABASE_URL ?? '';
  if (!base || base.includes('placeholder')) return '';
  const pid = `${campaignId}-${platform}-${Date.now()}`;
  return `${base}/functions/v1/track-event-2026-06-13/pixel.gif?cid=${encodeURIComponent(campaignId)}&p=${encodeURIComponent(platform)}&pid=${encodeURIComponent(pid)}`;
}

/**
 * Returns a click-tracking redirect URL.
 */
export function buildClickTrackingUrl(
  campaignId: string,
  platform: string,
  destinationUrl: string,
): string {
  const base = import.meta.env.VITE_SUPABASE_URL ?? '';
  if (!base || base.includes('placeholder')) return destinationUrl;
  return `${base}/functions/v1/track-event-2026-06-13/track-click?cid=${encodeURIComponent(campaignId)}&p=${encodeURIComponent(platform)}&url=${encodeURIComponent(destinationUrl)}`;
}
